import globalStyles from './styles/_global.scss';
import { ApiClient } from './utils/api-client.js';
import { isMobileScreen } from './utils/helper.js';
import { PopupDisplayMode, StorageKeys } from './utils/enums.js';
import { NotificationType } from './components/tui-favorites-notification.js';

import './components/popups/tui-favorites-popup-add-hotel.js';
import './components/tui-favorites-notification.js';

class FavoritesPopup extends HTMLElement {
  #lists = [];
  #locale = '';
  #tenant = '';
  #token = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.#observe();
  }

  async connectedCallback() {
    this.#locale = this.getAttribute('locale');
    this.#tenant = this.getAttribute('tenant');

    const isLegacyRootFontSize = window.getComputedStyle(document.documentElement).fontSize === '10px';
    if (isLegacyRootFontSize) {
      this.setAttribute('data-legacy-font', '');
    } else {
      this.removeAttribute('data-legacy-font');
    }
    const dataTheme = this.getAttribute('data-theme');
    if (!dataTheme || dataTheme === 'tuiLight') {
      this.setAttribute('data-theme', 'tui-light');
    }

    this.#render();
  }

  /**
   * Adds a new hotel to a favorite list.
   *
   * @param {Object} hotelDetails
   * @param {Array<Number>} position
   */
  async addHotel({ giataId, searchParams, placeholderListName = 'Meine neue Liste' }, position) {
    const popup = this.shadowRoot.querySelector('tui-favorites-popup-add-hotel');

    if (popup) {
      popup.setLists(this.#lists);
      popup.setDisplayMode();
      popup.open({
        position,
        data: { giataId, placeholderListName, searchParams },
        isUserLoggedIn: !!this.#token
      });
    } else {
      console.warn('Favorites popup add hotel element not found');
    }
  }

  /**
   * Removes a hotel from the favorites list and notifies the user on mobile devices.
   *
   * @param {Object} hotelData
   */
  async removeHotel({ giataId }) {
    for (const list of this.#lists) {
      const matchingEntry = list.entries.find((entry) => entry.giataId === giataId);

      if (matchingEntry) {
        await ApiClient('DELETE', `/${this.#tenant}/lists/${list.listId}/favorites/${giataId}`, {
          headers: this.#createHeaders()
        });

        const remainingHotels = list.entries.filter((entry) => entry.giataId !== giataId);
        const numOfRemovedHotels = list.entries.length - remainingHotels.length;

        list.entries = remainingHotels;

        if (isMobileScreen()) {
          const data = {
            giataId,
            listId: list.listId,
            listName: list.listName,
            numberOfListEntries: numOfRemovedHotels || 0,
            searchParams: matchingEntry.searchParams
          };

          this.#showNotification(NotificationType.REMOVED, data, 3000);
        }

        this.dispatchEvent(
          new CustomEvent('tui-favorites-popup.HotelRemoved', {
            bubbles: true,
            composed: true,
            detail: {
              giataId,
              numberOfEntries: numOfRemovedHotels,
              listId: list.listId,
              listName: list.listName,
            }
          })
        );
        this.#broadcastFavoritesChange();
      }
    }
  }

  #render() {
    this.shadowRoot.innerHTML = `
      <style>${globalStyles}</style>
      <tui-favorites-popup-add-hotel  tenant="${this.#tenant}" locale="${this.#locale}">
      </tui-favorites-popup-add-hotel>
      <tui-favorites-notification locale="${this.#locale}"></tui-favorites-notification>
    `;
  }

  #observe() {
    document.addEventListener('tui-favorites.AuthenticationStateChanged', async (event) => {
      this.#token = event.detail?.token || null;
    });

    document.addEventListener('tui-favorites.FavoritesListsChanged', async (event) => {
      if (event.detail.lists) {
        const isListDifferent = JSON.stringify(this.#lists) !== event.detail.lists;

        if (isListDifferent) {
          this.#lists = JSON.parse(event.detail.lists);

          this.#render();
        }
      }
    });

    this.addEventListener('tui-favorites-popup.AddEntryToFavoriteList', async (event) => {
      const { giataId, listId, searchParams, showNotification, isNewEntry } = event.detail;
      await this.#addEntryToList(listId, giataId, searchParams, isNewEntry);

      this.#onFavoritesChange(giataId, searchParams, showNotification);
      this.#broadcastFavoritesChange();
    });

    this.addEventListener('tui-favorites-popup.CreateFavoriteList', async (event) => {
      const { callback, listName } = event.detail;

      const { listId } = await this.#createList(listName);

      this.#broadcastFavoritesChange();
      this.dispatchEvent(
        new CustomEvent('tui-favorites-popup.FavoritesListsCreated', {
          bubbles: true,
          composed: true,
          detail: {
            listName
          }
        })
      );

      callback(listId);
    });

    this.addEventListener('tui-favorites-popup.OpenPopup', async (event) => {
      const { giataId, placeholderListName, searchParams } = event.detail;
      this.#openPopupWithDisplayMode(PopupDisplayMode.SELECT, [0, 0], giataId, searchParams, placeholderListName);
    });
  }

  async #addEntryToList(listId, giataId, searchParams, isNewEntry) {
    await ApiClient('POST', `/${this.#tenant}/lists/${listId}/favorites`, {
      body: {
        giataId,
        searchParams
      },
      headers: this.#createHeaders()
    });

    for (let i = 0; i < this.#lists.length; i++) {
      const list = this.#lists[i];
      if (list.listId !== listId && list.entries.some((entry) => entry.giataId === giataId)) {
        await this.#removeEntryFromFavoritesList(giataId, list.listId);
      }
    }

    const currentList = this.#lists.find((list) => list.listId === listId);
    if (!currentList.searchParams) {
      const { startDate, endDate, duration, rooms, airports } = JSON.parse(searchParams);
      const listSearchParams = { startDate, endDate, duration, rooms };

      if (airports) {
        listSearchParams.airports = airports;
      }

      await ApiClient('PUT', `/${this.#tenant}/lists/${listId}`, {
        body: { listName: currentList.listName, searchParams: JSON.stringify(listSearchParams) },
        headers: this.#createHeaders()
      });
    }

    // prepare event data for stats
    this.dispatchEvent(
      new CustomEvent('tui-favorites-popup.HotelAdded', {
        bubbles: true,
        composed: true,
        detail: {
          giataId,
          listId: listId,
          listName: currentList.listName,
          isNewList: isNewEntry // true if for new entry new list was also created
        }
      })
    );

    // sync with backend
    await this.#loadAndSetLists();
  }

  #broadcastFavoritesChange() {
    this.dispatchEvent(
      new CustomEvent('tui-favorites.FavoritesListsChanged', {
        bubbles: true,
        composed: true,
        detail: {
          lists: JSON.stringify(this.#lists)
        }
      })
    );
  }

  #createHeaders(headers) {
    const updatedHeaders = headers || {};
    if (this.#token) {
      updatedHeaders['x-tui-token'] = this.#token;
    }

    return updatedHeaders;
  }

  async #createList(listName) {
    const list = await ApiClient('POST', `/${this.#tenant}/lists`, {
      body: { listName },
      headers: this.#createHeaders()
    });

    list.entries = list.entries ?? [];

    this.#lists.unshift(list);

    if (!this.#token) {
      // Only set the storage key for non-authenticated users
      localStorage.setItem(StorageKeys.privateLists, this.#lists.map((list) => list.listId).join(','));
    }

    return list;
  }

  #onFavoritesChange(giataId, searchParams, showNotification = true) {
    const popup =
      document
        .querySelector('tui-favorites-popup-wrapper')
        ?.shadowRoot.querySelector('tui-favorites-popup-add-hotel') ||
      this.shadowRoot.querySelector('tui-favorites-popup-add-hotel');

    if (!popup) {
      console.warn('FavoritesPopup::#onFavoritesChange() -> popup element not found');
      return;
    }

    if (isMobileScreen()) {
      popup.close();

      if (showNotification) {
        const activeList = this.#lists.find((list) => list.entries.some((entry) => entry.giataId === giataId));
        const notificationType =
          this.#lists.length > 1 || this.#lists[0].entries.length > 1
            ? NotificationType.SAVED_WITH_CHANGE_TRIGGER
            : NotificationType.SAVED;

        const data = {
          giataId,
          listId: activeList?.listId,
          listName: activeList?.listName,
          searchParams
        };

        this.#showNotification(notificationType, data, 4000);
      }
    } else {
      popup.setLists(this.#lists);
    }
  }

  #openPopupWithDisplayMode(displayMode, position, giataId, searchParams, placeholderListName = '') {
    const popup = this.shadowRoot.querySelector('tui-favorites-popup-add-hotel');

    if (popup) {
      popup.setLists(this.#lists);
      popup.setDisplayMode(displayMode);
      popup.open({
        position,
        data: { giataId, placeholderListName, searchParams },
        isUserLoggedIn: !!this.#token
      });
    } else {
      console.warn('Favorites popup add hotel element not found');
    }
  }

  async #loadAndSetLists() {
    if (this.#token) {
      this.#lists = await ApiClient('GET', `/${this.#tenant}/lists`, {
        headers: this.#createHeaders()
      });
    } else {
      const listIds = localStorage.getItem(StorageKeys.privateLists)?.split(',').filter(Boolean) ?? [];

      if (listIds.length) {
        this.#lists = await ApiClient('GET', `/${this.#tenant}/lists`, {
          params: { ids: listIds }
        });
      }
    }
  }

  async #removeEntryFromFavoritesList(giataId, listId) {
    await ApiClient('DELETE', `/${this.#tenant}/lists/${listId}/favorites/${giataId}`, {
      headers: this.#createHeaders()
    });

    this.#lists.forEach((list) => {
      if (list.listId === listId) {
        list.entries = list.entries.filter((entry) => entry.giataId !== giataId);
      }
    });
  }

  #showNotification(notificationType, data, duration = 2500) {
    const notification = this.shadowRoot.querySelector('tui-favorites-notification');

    if (notification) {
      notification.show(notificationType, duration, data);
    } else {
      console.warn('FavoritesPopup::#showNotification() -> notification element not found');
    }
  }
}

customElements.define('tui-favorites-popup', FavoritesPopup);
